import React, { useEffect, useMemo, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useBackButton } from "@/lib/hooks";
import { useCatalog } from "@/state/CatalogContext";
import { fetchOrders } from "@/lib/api";
import { ALL_ORIGINS, findOrigin } from "@/components/OriginMap";
import type { Order } from "@/types";

const REGION: Record<string, "africa" | "americas" | "asia"> = {
  Ethiopia: "africa", Kenya: "africa", Rwanda: "africa", Burundi: "africa", Uganda: "africa", Tanzania: "africa", Yemen: "asia",
  Brazil: "americas", Colombia: "americas", Peru: "americas", Bolivia: "americas", Ecuador: "americas", Guatemala: "americas", Honduras: "americas",
  "Costa Rica": "americas", "El Salvador": "americas", Nicaragua: "americas", Panama: "americas", Mexico: "americas",
  Indonesia: "asia", Vietnam: "asia", India: "asia", "Papua New Guinea": "asia", China: "asia",
};

/** "Кофейный паспорт": a stamp for every coffee country the customer has received. */
export function PassportPage() {
  const { t, locale } = useI18n();
  const { goBack, navigate } = useRouter();
  useBackButton(goBack);
  const { products } = useCatalog();
  const [orders, setOrders] = useState<Order[] | null>(null);
  useEffect(() => {
    fetchOrders().then(setOrders).catch(() => setOrders([]));
  }, []);

  const stamps = useMemo(() => {
    const got = new Map<string, { date: string; product: string }>();
    for (const o of (orders ?? []).filter((x) => x.status === "delivered").sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
      for (const i of o.items) {
        const p = products.find((x) => x.id === i.productId);
        const origin = findOrigin(p?.attributes?.["Происхождение"] ?? "");
        if (origin && !got.has(origin.en)) got.set(origin.en, { date: o.createdAt, product: i.productName });
      }
    }
    return got;
  }, [orders, products]);

  const count = stamps.size;
  const regions = new Set([...stamps.keys()].map((k) => REGION[k]));
  const badges = [
    { key: "first", ok: count >= 1 },
    { key: "explorer", ok: count >= 3 },
    { key: "traveler", ok: count >= 5 },
    { key: "world", ok: regions.size >= 3 },
  ];

  return (
    <>
      <TopBar title={t("passport.title")} onBack={goBack} />
      <main className="page page--no-nav">
        <div className="passport-head">
          <img src="/brand/kms-logo.png" alt="" />
          <div>
            <span className="eyebrow">{t("passport.eyebrow")}</span>
            <strong>{t("passport.count", { count, total: ALL_ORIGINS.length })}</strong>
            <div className="brew__bar" style={{ marginTop: 8 }}><div style={{ width: `${(count / ALL_ORIGINS.length) * 100}%` }} /></div>
          </div>
        </div>

        <div className="section-heading"><span className="diamond" />{t("passport.badges")}</div>
        <div className="badges">
          {badges.map((b) => (
            <div key={b.key} className="badge-tile" data-ok={b.ok}>
              <span>{b.ok ? "◆" : "◇"}</span>
              <b>{t(`passport.badge.${b.key}`)}</b>
              <small>{t(`passport.badgeHint.${b.key}`)}</small>
            </div>
          ))}
        </div>

        <div className="section-heading"><span className="diamond" />{t("passport.stamps")}</div>
        {orders === null ? (
          <p>{t("common.loading")}</p>
        ) : (
          <div className="stamps">
            {ALL_ORIGINS.map((o) => {
              const s = stamps.get(o.en);
              return (
                <div key={o.en} className="stamp" data-ok={!!s}>
                  <b>{o[locale]}</b>
                  <small>{s ? new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { month: "short", year: "numeric" }).format(new Date(s.date)) : t("passport.notYet")}</small>
                </div>
              );
            })}
          </div>
        )}
        {count === 0 && orders !== null && (
          <button className="btn btn--primary" style={{ width: "100%", marginTop: 16 }} onClick={() => navigate({ name: "catalog", category: "coffee" })}>
            {t("passport.start")}
          </button>
        )}
      </main>
    </>
  );
}
